import React, { useState, useEffect } from "react";
import "./App.css";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { useCollection } from "react-firebase-hooks/firestore";
import {
  Container,
  Card,
  Button,
  Row,
  Col,
  Badge,
  Alert,
  Form,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faMoon,
  faArrowLeft,
  faChevronDown,
  faChevronUp,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import ImagePicker from "./components/ImagePicker";
import LessonForm from "./components/LessonForm";
import LessonList from "./components/LessonList";
import { firebaseApp } from "./firebase-config";

const NightSchool = () => {
  const [currentView, setCurrentView] = useState("list"); // 'list', 'add', 'edit'
  const [editingLesson, setEditingLesson] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [message, setMessage] = useState(null);

  const lessonsRef = collection(
    getFirestore(firebaseApp),
    "night-school-lessons"
  );
  const [lessons, loading, error] = useCollection(
    query(lessonsRef, orderBy("lesson_number", "asc")),
    {
      snapshotListenOptions: { includeMetadataChanges: true },
    }
  );

  const handleAddLesson = () => {
    setCurrentView("add");
    setEditingLesson(null);
  };

  const handleEditLesson = (lesson) => {
    setCurrentView("edit");
    setEditingLesson(lesson);
  };

  const handleLessonClick = (lesson) => {
    setSelectedLesson(lesson);
  };

  const handleClosePanel = () => {
    setSelectedLesson(null);
  };

  const handleDeleteLesson = async (lessonId) => {
    if (window.confirm("Are you sure you want to delete this lesson?")) {
      try {
        await deleteDoc(
          doc(getFirestore(firebaseApp), "night-school-lessons", lessonId)
        );

        // If the deleted lesson is currently selected, close the side panel
        if (selectedLesson?.id === lessonId) {
          setSelectedLesson(null);
        }

        setMessage({ type: "success", text: "Lesson deleted successfully!" });
        setTimeout(() => setMessage(null), 3000);
      } catch (error) {
        setMessage({
          type: "danger",
          text: "Error deleting lesson: " + error.message,
        });
        setTimeout(() => setMessage(null), 5000);
      }
    }
  };

  const handleBackToList = () => {
    setCurrentView("list");
    setEditingLesson(null);
    setSelectedLesson(null); // Close side panel when going back
  };

  const handleSave = async (lessonData) => {
    // Add confirmation for updates
    if (currentView === "edit" && editingLesson?.id) {
      if (!window.confirm("Are you sure you want to update this lesson?")) {
        return;
      }
    }

    try {
      if (currentView === "edit" && editingLesson?.id) {
        // Update existing lesson
        await updateDoc(
          doc(
            getFirestore(firebaseApp),
            "night-school-lessons",
            editingLesson.id
          ),
          lessonData
        );

        // If the updated lesson is currently selected in side panel, refresh it
        if (selectedLesson?.id === editingLesson.id) {
          setSelectedLesson({ id: editingLesson.id, ...lessonData });
        }

        setMessage({ type: "success", text: "Lesson updated successfully!" });
        setCurrentView("list");
        setEditingLesson(null);
      } else if (currentView === "add") {
        // Add new lesson
        await addDoc(lessonsRef, lessonData);
        setMessage({ type: "success", text: "Lesson added successfully!" });
        setCurrentView("list");
      }
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({
        type: "danger",
        text: "Error saving lesson: " + error.message,
      });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  if (loading) {
    return (
      <div className="App">
        <Container>
          <div style={{ marginTop: "45px", textAlign: "center" }}>
            <FontAwesomeIcon
              icon={faMoon}
              size="3x"
              style={{ color: "#F0D6B5", marginBottom: "20px" }}
            />
            <h3 style={{ color: "#635956" }}>Loading lessons...</h3>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <Container>
          <div style={{ marginTop: "45px" }}>
            <Alert variant="danger">
              Error loading lessons: {error.message}
            </Alert>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="App">
      <Container>
        <div style={{ marginTop: "45px" }}>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            {currentView !== "list" ? (
              <Button
                variant="outline-secondary"
                onClick={handleBackToList}
                style={{ borderColor: "#635956", color: "#635956" }}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Lessons
              </Button>
            ) : (
              <div>
                <h1 style={{ color: "#F0D6B5", marginBottom: "5px" }}>
                  <FontAwesomeIcon icon={faMoon} className="me-3" />
                  Night School
                </h1>
                <p style={{ color: "#635956", marginBottom: "0" }}>
                  Manage your sleep education lessons
                </p>
              </div>
            )}

            {currentView === "list" && (
              <Button
                onClick={handleAddLesson}
                style={{
                  backgroundColor: "#F0D6B5",
                  borderColor: "#F0D6B5",
                  color: "#635956",
                  fontWeight: "600",
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                New Lesson
              </Button>
            )}
          </div>

          {/* Messages */}
          {message && (
            <Alert variant={message.type} className="mb-4">
              {message.text}
            </Alert>
          )}

          {/* Content based on current view */}
          {currentView === "list" && (
            <LessonList
              lessons={lessons?.docs || []}
              selectedLesson={selectedLesson}
              onLessonClick={handleLessonClick}
              onClosePanel={handleClosePanel}
              onSave={handleSave}
              onDelete={handleDeleteLesson}
              onEdit={handleEditLesson}
            />
          )}

          {currentView === "add" && (
            <LessonForm onSave={handleSave} onCancel={handleBackToList} />
          )}

          {currentView === "edit" && editingLesson && (
            <LessonForm
              lesson={editingLesson}
              onSave={handleSave}
              onCancel={handleBackToList}
              isEdit={true}
            />
          )}
        </div>
      </Container>
    </div>
  );
};

export default NightSchool;
